import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "mt-8 border-t border-(--border-border-base-soft) pt-6",
    "data-testid": "shared-email-setting"
};
const _hoisted_2 = { class: "flex items-start justify-between gap-6" };
const _hoisted_3 = { class: "min-w-0" };
const _hoisted_4 = ["id"];
const _hoisted_5 = { class: "mt-1 text-sm text-(--colors-text-body-subtle)" };
const _hoisted_6 = ["id"];
const _hoisted_7 = {
    key: 1,
    class: "mt-2 text-sm text-(--colors-text-error-strong)",
    role: "alert",
    "data-testid": "shared-email-setting-error"
};
const _hoisted_8 = {
    key: 2,
    class: "mt-2 flex items-center gap-3",
    "data-testid": "shared-email-setting-load-error"
};
const _hoisted_9 = {
    class: "text-sm text-(--colors-text-error-strong)",
    role: "alert"
};
const _hoisted_10 = { "data-testid": "shared-email-setting-confirm" };
import { computed, ref, useId, watch } from "vue";
import { Button, Modal, ToggleSwitch } from "@eudonet/opal";
import { usePermissions } from "@/shared/composables/usePermissions";
import { useAppToast } from "@/shared/composables/useAppToast";
import { resolveApiErrorMessage } from "@/infrastructure/services/ApiErrorService";
import { logger } from "@/infrastructure/services/LoggerService";
import { getSharedEmailSetting, updateSharedEmailSetting, } from "../../../infrastructure/adapters/sharedEmailSettingService";
import { useSharedEmailSettingStore } from "../../../stores/useSharedEmailSettingStore";
// The only code the write can refuse with (OPHUB SharedEmailDisableRefusedException). Anything
// else falls back to the generic save message.
export default /*@__PURE__*/ _defineComponent({
    __name: 'SharedEmailSettingSection',
    setup(__props) {
        const SAVE_ERROR_KEYS = {
            "Settings.SharedEmail.DisableRefusedInUse": "workspace.settings.sharedEmail.errors.inUse",
        };
        const { toastSuccess } = useAppToast();
        const { can, canEditOphub, isReady } = usePermissions();
        const labelId = useId();
        const lockedReasonId = useId();
        const setting = ref({ enabled: false, canDisable: true });
        const status = ref("loading");
        const isSaving = ref(false);
        const isConfirmOpen = ref(false);
        const saveError = ref(null);
        const sharedEmailSettingCache = useSharedEmailSettingStore();
        // Defensive: the switch is locked while a load is in flight, so the screen cannot start a save
        // during one today. Were that to change, a GET resolving after a save would restore the pre-save
        // value. Bumped by every write.
        let writeSeq = 0;
        // OPHUB gates the write with KEYCLOAK_HUB_EDITOR_POLICY (SharedEmailSettingsController.Update),
        // which PermissionHelper projects as `ophub.edit` - for the OphubEditor role AND the legacy
        // ophub-editor / ophub-sysop client roles the policy also admits. NOT the AccessManager
        // capability: the two role sets are disjoint, so gating on it would lock the very persona the
        // server designates out of its own setting (R42).
        const canChange = canEditOphub;
        // Every authenticated user reaches this screen - the page's own load is OPUS legal-info, which
        // gates on nothing more - while this setting's GET takes OPHUB's reader policy. Without that
        // capability the section is not rendered at all, rather than showing a failed read and a retry
        // that can never succeed (OP-8214: a user who cannot administer the workspace sees it read-only
        // or not at all). The edit right implies the read one, so an editor always sees it.
        const canSeeSetting = computed(() => isReady.value && can("ophub.read"));
        const lockedReasonKey = computed(() => {
            // `isReady` too: the section is gone without a profile, but `can()` fails closed, so reading
            // this before one arrives would announce a denial to an entitled editor.
            if (status.value !== "ready" || !isReady.value)
                return null;
            // The app-wide "no edit rights" sentence, the one usePermissions serves for disabled controls -
            // not a setting-specific wording that would have to repeat which role is required.
            if (!canChange.value)
                return "common.permissions.noEditRights";
            if (setting.value.enabled && !setting.value.canDisable) {
                return "workspace.settings.sharedEmail.lockedInUse";
            }
            return null;
        });
        // Unknown is not "locked": while the profile or the setting is still loading the switch is inert
        // without claiming a reason, because `can()` fails closed and would otherwise tell an entitled
        // editor they have no rights (usePermissions.isReady).
        const isLocked = computed(() => status.value !== "ready" || !isReady.value || lockedReasonKey.value !== null);
        // The profile can land after this mounts, and `can()` fails closed until it does, so the read
        // waits for it instead of firing a 403 on every page load.
        watch(canSeeSetting, (visible) => {
            if (visible)
                void load();
        }, { immediate: true });
        async function load() {
            const seq = writeSeq;
            status.value = "loading";
            try {
                const loaded = await getSharedEmailSetting();
                if (seq !== writeSeq)
                    return;
                setting.value = loaded;
                status.value = "ready";
            }
            catch (error) {
                if (seq !== writeSeq)
                    return;
                // No fabricated value: showing "disabled" on a failed read would assert the opposite of the
                // truth on a workspace that has it on.
                logger.error("Failed to load the shared email setting", error);
                status.value = "failed";
            }
        }
        function onToggle(next) {
            if (isLocked.value || isSaving.value)
                return;
            saveError.value = null;
            if (next) {
                isConfirmOpen.value = true;
                return;
            }
            void save(false);
        }
        function closeConfirm() {
            if (isSaving.value)
                return;
            isConfirmOpen.value = false;
        }
        function onConfirmVisibility(open) {
            if (!open)
                closeConfirm();
        }
        async function confirmEnable() {
            await save(true);
        }
        async function save(enabled) {
            isSaving.value = true;
            writeSeq += 1;
            try {
                setting.value = await updateSharedEmailSetting(enabled);
                sharedEmailSettingCache.remember(setting.value.enabled);
                status.value = "ready";
                isConfirmOpen.value = false;
                toastSuccess("workspace.settings.sharedEmail.saved");
            }
            catch (error) {
                saveError.value = resolveApiErrorMessage(error, SAVE_ERROR_KEYS, "workspace.settings.sharedEmail.errors.save");
                isConfirmOpen.value = false;
                // The refusal means the server state is not what this screen was showing (a shared email was
                // created since it loaded), so re-read rather than restoring the value we came from.
                await load();
            }
            finally {
                isSaving.value = false;
            }
        }
        return (_ctx, _cache) => {
            return (canSeeSetting.value)
                ? (_openBlock(), _createElementBlock("section", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                            _createElementVNode("p", {
                                id: _unref(labelId),
                                class: "text-sm font-semibold text-(--colors-text-heading)"
                            }, _toDisplayString(_ctx.$t("workspace.settings.sharedEmail.label")), 9 /* TEXT, PROPS */, _hoisted_4),
                            _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.$t("workspace.settings.sharedEmail.hint")), 1 /* TEXT */),
                            (lockedReasonKey.value)
                                ? (_openBlock(), _createElementBlock("p", {
                                    key: 0,
                                    id: _unref(lockedReasonId),
                                    class: "mt-2 text-sm text-(--colors-text-body-subtle)",
                                    "data-testid": "shared-email-setting-locked-reason"
                                }, _toDisplayString(_ctx.$t(lockedReasonKey.value)), 9 /* TEXT, PROPS */, _hoisted_6))
                                : _createCommentVNode("v-if", true),
                            (saveError.value)
                                ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(saveError.value), 1 /* TEXT */))
                                : _createCommentVNode("v-if", true),
                            (status.value === 'failed')
                                ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                    _createElementVNode("p", _hoisted_9, _toDisplayString(_ctx.$t("workspace.settings.sharedEmail.errors.load")), 1 /* TEXT */),
                                    _createVNode(_unref(Button), {
                                        variant: "secondary",
                                        size: "sm",
                                        label: _ctx.$t('workspace.settings.retry'),
                                        "data-testid": "shared-email-setting-retry",
                                        onClick: load
                                    }, null, 8 /* PROPS */, ["label"])
                                ]))
                                : _createCommentVNode("v-if", true)
                        ]),
                        (status.value === 'ready' || isSaving.value)
                            ? (_openBlock(), _createBlock(_unref(ToggleSwitch), {
                                key: 0,
                                "model-value": setting.value.enabled,
                                "aria-disabled": isLocked.value,
                                "aria-busy": isSaving.value ? 'true' : undefined,
                                "aria-labelledby": _unref(labelId),
                                "aria-describedby": lockedReasonKey.value ? _unref(lockedReasonId) : undefined,
                                "data-testid": "shared-email-setting-toggle",
                                "onUpdate:modelValue": onToggle
                            }, null, 8 /* PROPS */, ["model-value", "aria-disabled", "aria-busy", "aria-labelledby", "aria-describedby"]))
                            : _createCommentVNode("v-if", true)
                    ]),
                    _createVNode(_unref(Modal), {
                        "model-value": isConfirmOpen.value,
                        size: "sm",
                        "footer-position": "right",
                        "onUpdate:modelValue": onConfirmVisibility
                    }, {
                        header: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_ctx.$t("workspace.settings.sharedEmail.confirm.title")), 1 /* TEXT */)
                        ]),
                        footer: _withCtx(() => [
                            _createVNode(_unref(Button), {
                                variant: "secondary",
                                label: _ctx.$t('common.cancel'),
                                disabled: isSaving.value,
                                "data-testid": "shared-email-setting-confirm-cancel",
                                onClick: closeConfirm
                            }, null, 8 /* PROPS */, ["label", "disabled"]),
                            _createVNode(_unref(Button), {
                                variant: "primary",
                                label: _ctx.$t('workspace.settings.sharedEmail.confirm.accept'),
                                loading: isSaving.value,
                                "data-testid": "shared-email-setting-confirm-accept",
                                onClick: confirmEnable
                            }, null, 8 /* PROPS */, ["label", "loading"])
                        ]),
                        default: _withCtx(() => [
                            _createElementVNode("p", _hoisted_10, _toDisplayString(_ctx.$t("workspace.settings.sharedEmail.confirm.body")), 1 /* TEXT */)
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["model-value"])
                ]))
                : _createCommentVNode("v-if", true);
        };
    }
});
